/**
 * Accueil, tout ce qui se trouve SOUS la ligne de flottaison.
 *
 * Le premier ecran ne change pas d'un pixel : il occupe toujours exactement un
 * viewport, avec le titre, le pseudo, les trois boutons et le fond anime. Un
 * joueur qui ne fait pas defiler ne verra jamais cette partie, et c'est voulu :
 * le parcours de jeu ne doit pas s'allonger d'un geste.
 *
 * Elle existe pour deux publics. Le visiteur qui hesite et veut savoir a quoi
 * il joue avant de cliquer. Et les moteurs de recherche, qui ne trouvaient
 * jusqu'ici qu'une page de quatre boutons sans un mot sur le jeu : pas de
 * contenu, pas d'indexation.
 */
import type { ReactElement } from 'react';
import { contentFor, contentPrefix, type Step } from '../content/index.js';
import { useUiStore } from '../store/uiStore.js';
import { SiteLink } from '../ui/SiteLink.js';

/**
 * Vignette d'etape : un vrai tableau du catalogue, avec la case dessinee
 * par-dessus dans l'etat de l'etape.
 *
 * Pas de captures d'ecran figees : elles vieilliraient a la premiere retouche
 * de l'interface, et il faudrait les refaire en dix-sept langues. Ces images
 * sont deja servies par le jeu, elles ne coutent donc pas un octet de plus au
 * catalogue.
 */
function StepFigure({ step }: { readonly step: Step }): ReactElement {
  return (
    <span className="step-card__figure" aria-hidden="true">
      <img
        className="step-card__image"
        src={`/paintings/${step.painting}.webp`}
        alt=""
        width={320}
        height={320}
        loading="lazy"
        decoding="async"
      />
      <span className="step-card__zone" data-stage={step.stage} />
      {step.stage === 'reveal' && <span className="step-card__zone step-card__zone--second" />}
    </span>
  );
}

export function HomeBelowTheFold(): ReactElement {
  const language = useUiStore((state) => state.language);
  const content = contentFor(language);
  const { home } = content;
  // Les pages de contenu vivent sous un prefixe de langue, mais SEULEMENT si
  // cette langue est traduite : sinon l'adresse promettrait ce qu'elle ne sert pas.
  const localised = (path: string): string => `${contentPrefix(language)}${path}`;

  return (
    <div className="home-more">
      <div className="home-more__inner">
        <section className="home-more__section">
          <h2 className="home-more__heading">{home.headings.intro}</h2>
          {home.intro.map((text) => (
            <p className="home-more__body" key={text.slice(0, 40)}>
              {text}
            </p>
          ))}
        </section>

        <section className="home-more__section">
          <h2 className="home-more__heading">{home.headings.steps}</h2>
          <ol className="step-grid">
            {home.steps.map((step, index) => (
              <li className="step-card" key={step.title}>
                <StepFigure step={step} />
                <h3 className="step-card__title">
                  <span className="step-card__number">{index + 1}</span>
                  {step.title}
                </h3>
                <p className="home-more__body">{step.text}</p>
              </li>
            ))}
          </ol>
          <p className="home-more__body">
            <SiteLink to={localised('/how-to-play')} className="legal__link">
              {home.links.rules}
            </SiteLink>
          </p>
        </section>

        <section className="home-more__section">
          <h2 className="home-more__heading">{home.headings.modes}</h2>
          <div className="mode-grid">
            {home.modes.map((mode) => (
              <article className="mode-card" key={mode.name}>
                <h3 className="mode-card__title">{mode.name}</h3>
                <p className="home-more__body">{mode.text}</p>
              </article>
            ))}
          </div>
          <p className="home-more__body">
            <SiteLink to={localised('/modes')} className="legal__link">
              {home.links.modes}
            </SiteLink>
          </p>
        </section>

        <section className="home-more__section">
          <h2 className="home-more__heading">{home.headings.faq}</h2>
          {/* Titre + paragraphe, et non un accordeon : un texte replie derriere
              un clic est bien present dans le document, mais il n'est lu par
              personne, et c'est justement la section qui doit repondre avant
              qu'on ait a chercher. */}
          {home.faq.map((entry) => (
            <div className="faq-entry" key={entry.question}>
              <h3 className="faq-entry__question">{entry.question}</h3>
              <p className="home-more__body">{entry.answer}</p>
            </div>
          ))}
        </section>

        <section className="home-more__section">
          <h2 className="home-more__heading">{home.headings.more}</h2>
          <p className="home-more__body">
            {content.blog.map((article, index) => (
              <span key={article.slug}>
                {index > 0 && ' · '}
                <SiteLink to={localised(`/blog/${article.slug}`)} className="legal__link">
                  {article.title}
                </SiteLink>
              </span>
            ))}
          </p>
        </section>
      </div>
    </div>
  );
}
